import { SingleSelectCombobox } from '@/components/form-components/SingleSelectCombobox'
import { Badge } from '@/components/shadcn/badge'
import { Loader } from '@/components/prompt-kit/loader'
import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/shadcn/sheet'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/shadcn/dialog'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { useRestClient } from '@/contexts/QueryProvider'
import { useHeader } from '@/layouts/MainLayout/HeaderContext'
import { IMG_BASE_PATH } from '@/const'
import { isAgenticApp } from '@/util/appTypes'
import { cn } from '@/lib/utils'
import { IconSurveyScale5Outline } from '@icons/IconSurveyScale5Outline'
import { IconCommentOutline } from '@icons/IconCommentOutline'
import { IconViewOutline } from '@icons/IconViewOutline'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { IconEnterLeftOutline } from '@icons/IconEnterLeftOutline'
import { IconEnterRightOutline } from '@icons/IconEnterRightOutline'
import { IconPlusOutline } from '@icons/IconPlusOutline'
import { IconSearchOutline } from '@icons/IconSearchOutline'
import { IconBookOutline } from '@icons/IconBookOutline'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/shadcn/command'
import { ArrowDown, Check, MessageSquare, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AI_CHAT_CONTEXT_DRAG_MIME, ChatInput, ChatInputSendPayload } from './ChatInput'
import { ChatMessageItem, EmptyState } from './ChatMessages'
import { SavedChatMeta } from './types'
import { useAiChatComponentContext } from './useAiChatComponentContext'
import { AiChatProvider, ChatContextPayloadItem, useAiChat } from './useAiChat'
import { useAutoScroll, useAutoScrollEffect } from './useAutoScroll'
import { Separator } from '@/components/shadcn/separator'
import { MAIN_HEADER_HEIGHT } from '@/catalog-apps/components/config'
import { ComponentRenderer } from '../AppPage2/ContentPanel/TileGrid/ComponentRenderer'
import { useQueryClient } from '@tanstack/react-query'
import { ReactComponent as PwcLogo } from '@/assets/svg/pwc__logo_v2.svg'
import { Link } from 'react-router'

// ============================================================================
// Main Export - Sheet Trigger
// ============================================================================

type AppSupportMenuAiChatProps = {
  variant?: 'sidebar' | 'header'
  sidebarOpen?: boolean
}

const OPEN_AI_CHAT_EVENT = 'open-app-support-ai-chat'
const AI_CHAT_SIDEBAR_EXPANDED_WIDTH_PX = 320
const AI_CHAT_SIDEBAR_COLLAPSED_WIDTH_PX = 56

export function requestOpenAiChatSheet() {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(OPEN_AI_CHAT_EVENT))
}

export function AppSupportMenuAiChat({ variant = 'sidebar', sidebarOpen = true }: AppSupportMenuAiChatProps) {
  const { appKey, projectId } = useAppContext()
  const { getWorkspacesApiGetWorkspace } = useRestClient()
  const { data: workspace } = getWorkspacesApiGetWorkspace.useQuery(
    { path: { appKey: appKey!, projectId: projectId! } },
    { enabled: !!appKey && !!projectId }
  )
  const appTypes = (workspace as unknown as { app_types?: number } | undefined)?.app_types ?? 0
  const { isSelectingContext, startSelection, finishSelection } = useAiChatComponentContext()
  const handleDoneSelection = () => {
    finishSelection()
    requestOpenAiChatSheet()
  }

  if (!isAgenticApp(appTypes)) {
    return null
  }

  if (isSelectingContext) {
    if (variant === 'header') {
      return (
        <>
          <TooltipButton tooltip="Finish selecting context components">
            <Button size="sm" className="h-8" onClick={handleDoneSelection}>
              Done
            </Button>
          </TooltipButton>
          <Separator style={{ height: `${MAIN_HEADER_HEIGHT}px` }} orientation="vertical" />
        </>
      )
    }

    return (
      <button
        aria-label="Finish selecting AI context"
        className="inline-flex cursor-pointer items-center gap-x-2 whitespace-nowrap text-foreground hover:text-primary"
        onClick={handleDoneSelection}
      >
        <Check className="size-4" />
        <span className={`${!sidebarOpen ? 'invisible' : ''}`}>Done</span>
      </button>
    )
  }

  if (variant === 'header') {
    return (
      <>
        <DropdownMenu>
          <TooltipButton tooltip="AI Agent Chat actions">
            <DropdownMenuTrigger asChild>
              <Button
                aria-label="AI live chat actions"
                variant="ghost"
                size="icon"
                // className="relative size-8 rounded-full text-primary hover:bg-primary/10 hover:text-primary"
                className="relative"
              >
                <IconCommentOutline className="size-6" />
                <span className="absolute left-1/2 top-1/2 size-4 -translate-x-1/2 -translate-y-[55%] text-2xs font-bold">
                  AI
                </span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipButton>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>AI actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={requestOpenAiChatSheet}>Open chat</DropdownMenuItem>
            <DropdownMenuItem onSelect={startSelection}>Add to context</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Separator style={{ height: `${MAIN_HEADER_HEIGHT}px` }} orientation="vertical" />
      </>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="Open AI live chat actions"
          className="inline-flex cursor-pointer items-center gap-x-2 whitespace-nowrap text-foreground hover:text-primary"
        >
          <IconSurveyScale5Outline className="size-4" />
          <span className={`${!sidebarOpen ? 'invisible' : ''}`}>AI live chat</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="right" align="start">
        <DropdownMenuLabel>AI actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={requestOpenAiChatSheet}>Open chat</DropdownMenuItem>
        <DropdownMenuItem onSelect={startSelection}>Add to context</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function AppSupportMenuAiChatSheetHost() {
  const { appKey, projectId } = useAppContext()
  const { getWorkspacesApiGetWorkspace } = useRestClient()
  const { data: workspace } = getWorkspacesApiGetWorkspace.useQuery(
    { path: { appKey: appKey!, projectId: projectId! } },
    { enabled: !!appKey && !!projectId }
  )
  const appTypes = (workspace as unknown as { app_types?: number } | undefined)?.app_types ?? 0
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleOpen = () => setOpen(true)
    window.addEventListener(OPEN_AI_CHAT_EVENT, handleOpen)

    return () => {
      window.removeEventListener(OPEN_AI_CHAT_EVENT, handleOpen)
    }
  }, [])

  if (!isAgenticApp(appTypes)) {
    return null
  }

  return (
    <AiChatProvider isOpen={open}>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="!pointer-events-auto flex h-full !w-screen !max-w-none flex-col gap-0 p-0">
          <AiChatSheetLayout />
        </SheetContent>
      </Sheet>
    </AiChatProvider>
  )
}

export function EmbeddedAiChat({ agentKey }: { agentKey: string }) {
  return (
    <AiChatProvider key={agentKey} isOpen={Boolean(agentKey)} fixedAgentKey={agentKey}>
      <EmbeddedAiChatLayout />
    </AiChatProvider>
  )
}

function AiChatSheetLayout() {
  const {
    chatHistoryId,
    savedChats,
    loadChat,
    deleteChat,
    clearAllHistory,
    startNewChat,
    isHistoryLoading,
    isLoadingHistoryChat,
    isHistoryMutating,
  } = useAiChat()
  const { selectedContext, removeSelection, clearSelection } = useAiChatComponentContext()
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const headerCenterOffsetPx = isSidebarOpen
    ? AI_CHAT_SIDEBAR_EXPANDED_WIDTH_PX / 2
    : AI_CHAT_SIDEBAR_COLLAPSED_WIDTH_PX / 2

  return (
    <>
      <SheetHeader style={{ height: `${MAIN_HEADER_HEIGHT}px` }} className="shrink-0 bg-background pl-2 pr-6 shadow-md">
        <AiChatSheetHeaderContent centerOffsetPx={headerCenterOffsetPx} />
      </SheetHeader>
      <div className="flex min-h-0 flex-1 overflow-hidden">
        <UnifiedAiChatSidebar
          isOpen={isSidebarOpen}
          onOpenChange={setIsSidebarOpen}
          savedChats={savedChats}
          activeChatId={chatHistoryId}
          isHistoryLoading={isHistoryLoading}
          isHistoryMutating={isHistoryMutating}
          isLoadingHistoryChat={isLoadingHistoryChat}
          onSelectChat={loadChat}
          onNewChat={startNewChat}
          onDeleteChat={deleteChat}
          onClearHistory={clearAllHistory}
          selectedContext={selectedContext}
          onRemoveSelection={removeSelection}
          onClearSelection={clearSelection}
        />
        <AiChatContent />
      </div>
    </>
  )
}

function EmbeddedAiChatLayout() {
  const {
    chatHistoryId,
    savedChats,
    loadChat,
    deleteChat,
    clearAllHistory,
    startNewChat,
    isHistoryLoading,
    isLoadingHistoryChat,
    isHistoryMutating,
  } = useAiChat()
  const { selectedContext, removeSelection, clearSelection } = useAiChatComponentContext()
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)

  return (
    <div className="flex h-full min-h-0 overflow-hidden rounded-md border bg-background">
      <UnifiedAiChatSidebar
        isOpen={isSidebarOpen}
        onOpenChange={setIsSidebarOpen}
        savedChats={savedChats}
        activeChatId={chatHistoryId}
        isHistoryLoading={isHistoryLoading}
        isHistoryMutating={isHistoryMutating}
        isLoadingHistoryChat={isLoadingHistoryChat}
        onSelectChat={loadChat}
        onNewChat={startNewChat}
        onDeleteChat={deleteChat}
        onClearHistory={clearAllHistory}
        selectedContext={selectedContext}
        onRemoveSelection={removeSelection}
        onClearSelection={clearSelection}
      />
      <AiChatContent messageSurfaceClassName="bg-offwhite" />
    </div>
  )
}

// ============================================================================
// Sheet Header Content
// ============================================================================

function AiChatSheetHeaderContent({ centerOffsetPx }: { centerOffsetPx: number }) {
  const { botType, setBotType } = useAiChat()
  const { appId } = useAppContext()
  const { coBrandingImageKey, title1, title2, titleUrl } = useHeader()
  const { getAgentApiGetAgents } = useRestClient()
  const applicationId = appId ?? -1
  const { data: agents } = getAgentApiGetAgents.useQuery({
    path: { applicationId },
  })

  const agentOptions = useMemo(
    () =>
      (agents ?? []).map((agent) => ({
        value: agent.key || String(agent.id),
        label: agent.name || agent.key || `Agent #${agent.id}`,
        description: agent.description || undefined,
      })),
    [agents]
  )

  useEffect(() => {
    if (agentOptions.length === 0) return
    if (!agentOptions.some((option) => option.value === botType)) {
      setBotType(String(agentOptions[0].value))
    }
  }, [agentOptions, botType, setBotType])

  const title = (
    <div className="group flex shrink-0 items-center gap-2">
      <span>{title1 ?? 'Data Modelling Platform'}</span>
      {title2 && (
        <>
          <Separator
            orientation="vertical"
            className="inline-block h-4 w-[2px] rounded-b-full rounded-t-full bg-foreground group-hover:bg-primary"
          />
          <span>{title2}</span>
        </>
      )}
    </div>
  )

  return (
    <div className="relative flex items-center gap-3">
      <div className="flex min-h-0 items-center">
        {coBrandingImageKey ? (
          <img
            className="h-[29px] w-auto object-contain"
            alt="Data Modelling Platform"
            src={`${IMG_BASE_PATH}${coBrandingImageKey}`}
          />
        ) : (
          <span className="inline-flex items-center justify-center p-2.5">
            <PwcLogo
              className="block aspect-[360/180] h-6 w-12 min-w-[48px] shrink-0 fill-current text-black dark:text-white"
              aria-label="Data Modelling Platform"
              role="img"
            />
          </span>
        )}
        {/* <Separator orientation="vertical" style={{ height: `${MAIN_HEADER_HEIGHT}px` }} /> */}
        <div className="flex translate-y-[0.03rem] items-center text-[1.1rem] [&>a]:text-foreground [&>a]:hover:text-primary">
          {titleUrl ? <Link to={titleUrl}>{title}</Link> : title}
        </div>
      </div>
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-[margin-left] duration-300 ease-in-out"
        style={{ marginLeft: `${centerOffsetPx}px` }}
      >
        <SheetTitle className="sr-only">AI live chat</SheetTitle>
        <SheetDescription className="sr-only">Ask anything about the app</SheetDescription>
        <div className="pointer-events-auto w-[300px] max-w-[40vw]">
          <SingleSelectCombobox
            options={agentOptions}
            selectedValue={botType || null}
            onSelectionChange={(value) => setBotType(String(value ?? ''))}
            placeholder="Select agent"
            searchPlaceholder="Search agents..."
            emptyMessage="No agents found."
            className="h-8 text-base font-bold"
            allowClear={false}
            disabled={agentOptions.length === 0}
            triggerVariant="secondary"
            portalPopover
          />
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Chat Content
// ============================================================================

/**
 * Main chat content component.
 * Orchestrates the chat UI with messages, input, and scroll behavior.
 */
function AiChatContent({ messageSurfaceClassName }: { messageSurfaceClassName?: string }) {
  // Chat logic (SignalR, messages, sending)
  const {
    isConnected,
    isSubmitting,
    isStoppingGeneration,
    canStopGeneration,
    workerHasStarted,
    showLongWaitHint,
    contextState,
    isContextCompacting,
    showContextCompactedNotice,
    showContextSkippedNotice,
    lastContextEvent,
    messages,
    sendMessage,
    stopGeneration,
    clearMessages,
    chatHistoryId,
  } = useAiChat()
  const { selectedContext } = useAiChatComponentContext()
  const selectedContextMap = useMemo(
    () => Object.fromEntries(selectedContext.map((entry) => [entry.selectionKey, entry])),
    [selectedContext]
  )

  // Scroll management
  const {
    containerRef,
    anchorRef,
    showScrollButton,
    userHasScrolledUp,
    scrollToBottom,
    handleScroll,
    resetScrollState,
  } = useAutoScroll()
  const lastChatHistoryIdRef = useRef(chatHistoryId)
  const shouldForceScrollAfterSwitchRef = useRef(false)

  // Auto-scroll effect when messages change
  useAutoScrollEffect({
    messages,
    userHasScrolledUp,
    scrollToBottom,
  })

  // Session switches can hydrate a long message list asynchronously.
  // Force one deterministic bottom scroll after the new session messages render.
  useEffect(() => {
    if (lastChatHistoryIdRef.current === chatHistoryId) return

    lastChatHistoryIdRef.current = chatHistoryId
    shouldForceScrollAfterSwitchRef.current = true
    resetScrollState()
  }, [chatHistoryId, resetScrollState])

  useEffect(() => {
    if (!shouldForceScrollAfterSwitchRef.current) return
    if (messages.length === 0) return

    shouldForceScrollAfterSwitchRef.current = false
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottom(true)
      })
    })
  }, [messages.length, scrollToBottom])

  // Handle sending a message
  const handleSendMessage = useCallback(
    (payload: ChatInputSendPayload) => {
      const contextItems = payload.selectedContextKeys
        .map((selectionKey) => selectedContextMap[selectionKey])
        .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
        .map(buildContextPayloadItem)
        .slice(0, MAX_CONTEXT_ITEM_COUNT_PER_MESSAGE)

      const messageForWorker = buildMessageWithContextFallback(payload.displayText, contextItems)
      resetScrollState()
      sendMessage({
        message: messageForWorker,
        displayMessage: payload.displayText,
        contextItems,
      })
      // Instant scroll so the pending assistant bubble is visible immediately
      requestAnimationFrame(() => scrollToBottom(true))
    },
    [resetScrollState, scrollToBottom, selectedContextMap, sendMessage]
  )

  // Show loading state while connecting
  if (!isConnected) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader variant="typing" size="lg" />
      </div>
    )
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      {/* Messages area - scrollable */}
      <div
        ref={containerRef}
        className={cn('relative flex-1 overflow-y-auto', messageSurfaceClassName)}
        onScroll={handleScroll}
      >
        <div className="space-y-6 px-6 py-6">
          {messages.length === 0 ? (
            <EmptyState />
          ) : (
            messages.map((message, index) => {
              const isLast = index === messages.length - 1
              const isStreamingAssistant = isLast && message.role === 'assistant' && Boolean(message.isStreaming)
              const isStoppingThisMessage = isStoppingGeneration && isStreamingAssistant

              return (
                <ChatMessageItem
                  key={message.id}
                  message={message}
                  isLast={isLast}
                  isStopping={isStoppingThisMessage}
                  agentConnected={isStreamingAssistant ? workerHasStarted : false}
                  showLongWaitHint={isStreamingAssistant && showLongWaitHint}
                  canAbortTurn={isStreamingAssistant && canStopGeneration}
                  isContextCompacting={isStreamingAssistant && isContextCompacting}
                  showContextCompactedNotice={isLast && message.role === 'assistant' && showContextCompactedNotice}
                  showContextSkippedNotice={isLast && message.role === 'assistant' && showContextSkippedNotice}
                  contextState={contextState}
                  onAbortTurn={() => {
                    void stopGeneration()
                  }}
                />
              )
            })
          )}
          {/* Scroll anchor */}
          <div ref={anchorRef} />
        </div>

        {/* Floating scroll-to-bottom button */}
        <ScrollToBottomButton show={showScrollButton} onClick={() => scrollToBottom(false)} />
      </div>

      {/* Input area - fixed at bottom */}
      <ChatInput
        isSubmitting={isSubmitting}
        isStopping={isStoppingGeneration}
        canStop={canStopGeneration}
        workerHasStarted={workerHasStarted}
        hasMessages={messages.length > 0}
        onSendMessage={handleSendMessage}
        onStop={() => {
          void stopGeneration()
        }}
        onClearMessages={clearMessages}
        contextState={contextState}
        lastContextEvent={lastContextEvent}
      />
    </div>
  )
}

type SidebarPanel = 'history' | 'context'

function UnifiedAiChatSidebar({
  isOpen,
  onOpenChange,
  savedChats,
  activeChatId,
  isHistoryLoading = false,
  isHistoryMutating = false,
  isLoadingHistoryChat = false,
  onSelectChat,
  onNewChat,
  onDeleteChat,
  onClearHistory,
  selectedContext,
  onRemoveSelection,
  onClearSelection,
}: {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  savedChats: SavedChatMeta[]
  activeChatId: string | null
  isHistoryLoading?: boolean
  isHistoryMutating?: boolean
  isLoadingHistoryChat?: boolean
  onSelectChat: (chatId: string) => Promise<void>
  onNewChat: () => void
  onDeleteChat: (chatId: string) => Promise<void>
  onClearHistory: () => Promise<void>
  selectedContext: ReturnType<typeof useAiChatComponentContext>['selectedContext']
  onRemoveSelection: (selectionKey: string) => void
  onClearSelection: () => void
}) {
  const [activePanel, setActivePanel] = useState<SidebarPanel>('history')
  const [searchValue, setSearchValue] = useState('')
  const historySearchInputRef = useRef<HTMLInputElement | null>(null)
  const [previewTarget, setPreviewTarget] = useState<(typeof selectedContext)[number] | null>(null)
  const tileContainerRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()

  const openHistoryPanel = useCallback(() => {
    setActivePanel('history')
    onOpenChange(true)
  }, [onOpenChange])

  const openContextPanel = useCallback(() => {
    setActivePanel('context')
    onOpenChange(true)
  }, [onOpenChange])

  const handleStartNewChat = useCallback(() => {
    onNewChat()
    setSearchValue('')
    onOpenChange(true)
  }, [onNewChat, onOpenChange])

  const handleDeleteHistoryItem = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, chatId: string) => {
      event.preventDefault()
      event.stopPropagation()
      if (chatId === activeChatId) return
      void onDeleteChat(chatId)
    },
    [activeChatId, onDeleteChat]
  )

  const handleOpenPreview = useCallback(
    (entry: (typeof selectedContext)[number]) => {
      if (Array.isArray(entry.queryKey) && entry.queryKey.length > 0) {
        queryClient.setQueryData(entry.queryKey, entry.response)
      }
      setPreviewTarget(entry)
    },
    [queryClient]
  )

  useEffect(() => {
    if (!isOpen || activePanel !== 'history') return
    requestAnimationFrame(() => {
      historySearchInputRef.current?.focus()
    })
  }, [activePanel, isOpen])

  return (
    <>
      <aside
        className={cn(
          'h-full overflow-hidden border-r bg-background/50 transition-[width] duration-300 ease-in-out',
          isOpen ? 'w-80' : 'w-14'
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-12 items-center p-2">
            <TooltipButton tooltip={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-8 w-full justify-start gap-2 px-2 text-xs text-muted-foreground active:translate-y-0',
                  !isOpen && 'h-9 justify-center px-0'
                )}
                onClick={() => onOpenChange(!isOpen)}
                aria-label="Toggle AI chat sidebar"
              >
                <IconEnterRightOutline className={cn('size-4', { hidden: isOpen })} />
                <span className={cn('text-xs', { hidden: !isOpen })}>
                  Collapse
                  <IconEnterLeftOutline className="ml-2 inline-block size-4" />
                </span>
              </Button>
            </TooltipButton>
          </div>

          <div className="space-y-1 border-b p-2">
            <SidebarNavButton
              isOpen={isOpen}
              label="Start new chat"
              icon={<IconPlusOutline className="size-4" />}
              onClick={handleStartNewChat}
              tooltip="Start new chat"
            />
            <SidebarNavButton
              isOpen={isOpen}
              isActive={activePanel === 'history'}
              label="Search chats"
              icon={<IconSearchOutline className="size-4" />}
              onClick={openHistoryPanel}
              tooltip="Search chats"
            />
            <SidebarNavButton
              isOpen={isOpen}
              isActive={activePanel === 'context'}
              label="Available context"
              icon={<IconBookOutline className="size-4" />}
              onClick={openContextPanel}
              tooltip="Available context"
              badgeCount={selectedContext.length}
            />
          </div>

          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col transition-opacity duration-200',
              isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
            aria-hidden={!isOpen}
          >
            {activePanel === 'history' ? (
              <>
                <Command className="h-full rounded-none border-0 bg-transparent">
                  <CommandInput
                    placeholder="Search chats..."
                    className="h-9 text-xs"
                    ref={historySearchInputRef}
                    value={searchValue}
                    onValueChange={setSearchValue}
                    disabled={isHistoryLoading}
                  />
                  <CommandList className="max-h-none flex-1 overflow-y-auto p-2">
                    <CommandEmpty>
                      {isHistoryLoading
                        ? 'Loading chat history...'
                        : savedChats.length === 0
                          ? 'No chat history yet.'
                          : 'No chats found.'}
                    </CommandEmpty>
                    <CommandGroup className="p-0">
                      {savedChats.map((chat) => (
                        <CommandItem
                          key={chat.id}
                          value={`${chat.title} ${chat.preview}`}
                          onSelect={() => {
                            void onSelectChat(chat.id)
                            setSearchValue('')
                          }}
                          className={cn(
                            'group mb-1 items-start gap-1 rounded-md p-0 text-foreground data-[selected=true]:bg-muted data-[selected=true]:text-foreground',
                            activeChatId === chat.id ? 'bg-primary/10' : ''
                          )}
                        >
                          <div className="flex min-w-0 flex-1 items-start gap-2 px-2 py-2">
                            <MessageSquare className="mt-0.5 size-4 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1">
                                <span className="truncate text-sm">{chat.title}</span>
                                {activeChatId === chat.id && <Check className="size-3.5 text-primary" />}
                              </div>
                              <p className="truncate text-xs opacity-70">
                                {formatSidebarDate(chat.lastUpdatedAt)} · {chat.messageCount} messages
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className={cn(
                              'mr-1 mt-1 size-6 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 disabled:opacity-30 group-hover:opacity-100',
                              activeChatId === chat.id ? 'hidden' : ''
                            )}
                            onMouseDown={(event) => {
                              event.preventDefault()
                              event.stopPropagation()
                            }}
                            onClick={(event) => handleDeleteHistoryItem(event, chat.id)}
                            disabled={activeChatId === chat.id || isHistoryMutating || isLoadingHistoryChat}
                            title={activeChatId === chat.id ? "Can't delete active chat" : 'Delete chat'}
                          >
                            <Trash2 className="size-3" />
                          </Button>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>

                {savedChats.length > 0 && (
                  <div className="border-t p-3">
                    <Button
                      variant="ghost"
                      className="h-8 w-full justify-start text-xs text-destructive"
                      onClick={() => void onClearHistory()}
                      disabled={isHistoryMutating || isLoadingHistoryChat}
                    >
                      <Trash2 className="mr-2 size-4" />
                      <span>Clear All History</span>
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3">
                  {selectedContext.length === 0 && (
                    <div className="rounded-md border border-dashed px-3 py-4 text-xs text-muted-foreground">
                      No context added yet. Use "Add to context" in the AI menu.
                    </div>
                  )}
                  {selectedContext.map((entry) => {
                    const uiSchema = getUiSchemaPreview(entry.response, entry.componentType)

                    return (
                      <div
                        key={entry.selectionKey}
                        className="inline-flex items-center justify-between gap-1 rounded-md border bg-background px-2 py-1 text-xs"
                        draggable
                        onDragStart={(event) => {
                          event.dataTransfer.setData(AI_CHAT_CONTEXT_DRAG_MIME, entry.selectionKey)
                          event.dataTransfer.effectAllowed = 'copy'
                        }}
                      >
                        <div className="flex-col">
                          {uiSchema.title && <div className="font-medium">{uiSchema.title}</div>}
                          {uiSchema.description && (
                            <div className="line-clamp-1 text-2xs font-light">{uiSchema.description}</div>
                          )}
                          <div className="pb-1 pt-2">
                            <Badge variant="secondary" className="h-4 whitespace-nowrap text-2xs font-bold">
                              {entry.componentType}
                            </Badge>
                          </div>
                        </div>

                        <div className="flex flex-col">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="ml-auto size-6"
                            onClick={() => handleOpenPreview(entry)}
                            title={`View ${entry.componentType} (${entry.componentId})`}
                          >
                            <IconViewOutline className="size-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-6"
                            onClick={() => onRemoveSelection(entry.selectionKey)}
                            title={`Remove ${entry.componentType} (${entry.componentId})`}
                          >
                            <IconCloseOutline className="size-3.5" />
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className="border-t px-3 py-2">
                  <div className="rounded-md border bg-secondary px-3 py-2 text-xs text-foreground">
                    To add more components to the available context, close the chat inteface, click the{' '}
                    <span
                      title="AI menu"
                      className="relative inline-flex size-4 shrink-0 rounded-full align-middle text-primary hover:bg-primary/10 hover:text-primary"
                    >
                      <IconCommentOutline className="size-4" />
                      <span className="pointer-events-none absolute left-1/2 top-[43.75%] -translate-x-1/2 -translate-y-1/2 text-[8px] font-bold leading-none">
                        AI
                      </span>
                    </span>
                    button in the header, choose <span className="font-semibold">Add to context</span>, select
                    components on any app page, then click <span className="font-semibold">Done</span>.
                  </div>
                </div>

                {selectedContext.length > 0 && (
                  <div className="border-t p-3">
                    <Button
                      variant="ghost"
                      className="h-8 w-full justify-start text-xs text-destructive"
                      onClick={onClearSelection}
                    >
                      <Trash2 className="mr-2 size-4" />
                      <span>Clear All Context</span>
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </aside>
      <SelectedContextPreviewDialog
        previewTarget={previewTarget}
        onOpenChange={(open) => {
          if (!open) setPreviewTarget(null)
        }}
        tileContainerRef={tileContainerRef}
      />
    </>
  )
}

function SidebarNavButton({
  isOpen,
  isActive = false,
  label,
  icon,
  tooltip,
  badgeCount,
  onClick,
}: {
  isOpen: boolean
  isActive?: boolean
  label: string
  icon: React.ReactNode
  tooltip: string
  badgeCount?: number
  onClick: () => void
}) {
  const hasBadge = typeof badgeCount === 'number'
  const badgeLabel = hasBadge ? (badgeCount > 99 ? '99+' : String(badgeCount)) : null

  return (
    <TooltipButton tooltip={tooltip} side="right">
      <Button
        variant={isActive ? 'secondary' : 'ghost'}
        className={cn('h-8 w-full justify-start gap-2 px-2 text-xs', !isOpen && 'h-9 justify-center px-0')}
        onClick={onClick}
        aria-label={label}
      >
        <span className="relative inline-flex">
          {icon}
          {hasBadge && !isOpen && (
            <span className="pointer-events-none absolute -right-2 -top-2 inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-info px-1 text-[10px] font-semibold leading-none text-info-foreground">
              {badgeLabel}
            </span>
          )}
        </span>
        <span className={cn('inline-flex items-center gap-2 truncate', !isOpen && 'hidden')}>
          <span className="truncate">{label}</span>
          {hasBadge && (
            <span className="inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-info px-1.5 text-[10px] font-semibold leading-none text-info-foreground">
              {badgeLabel}
            </span>
          )}
        </span>
      </Button>
    </TooltipButton>
  )
}

function formatSidebarDate(timestamp: number): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return date.toLocaleDateString()
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object'
}

function getNestedString(root: unknown, path: string[]): string | undefined {
  let current: unknown = root
  for (const segment of path) {
    if (!isRecord(current)) return undefined
    current = current[segment]
  }
  return typeof current === 'string' ? current : undefined
}

function getUiSchemaPreview(
  response: unknown,
  componentType: ReturnType<typeof useAiChatComponentContext>['selectedContext'][number]['componentType']
): { title?: string; description?: string } {
  const perTypePaths: Record<string, { title: string[]; description: string[] }> = {
    form: {
      title: ['uiSchema', 'schema', 'title'],
      description: ['uiSchema', 'schema', 'description'],
    },
    'data-form': {
      title: ['uiSchema', 'schema', 'title'],
      description: ['uiSchema', 'schema', 'description'],
    },
    table: {
      title: ['uiSchema', 'tableConfig', 'title'],
      description: ['uiSchema', 'tableConfig', 'description'],
    },
  }

  const selectedPath = perTypePaths[componentType]
  const titleFromTypePath = selectedPath ? getNestedString(response, selectedPath.title) : undefined
  const descriptionFromTypePath = selectedPath ? getNestedString(response, selectedPath.description) : undefined

  return {
    title: titleFromTypePath ?? getNestedString(response, ['uiSchema', 'title']),
    description: descriptionFromTypePath ?? getNestedString(response, ['uiSchema', 'description']),
  }
}

function SelectedContextPreviewDialog({
  previewTarget,
  onOpenChange,
  tileContainerRef,
}: {
  previewTarget: ReturnType<typeof useAiChatComponentContext>['selectedContext'][number] | null
  onOpenChange: (open: boolean) => void
  tileContainerRef: React.RefObject<HTMLDivElement>
}) {
  return (
    <Dialog open={Boolean(previewTarget)} onOpenChange={onOpenChange}>
      <DialogContent className="z-[1001] max-h-[calc(100vh-40px)] max-w-[calc(100vw-40px)] bg-background p-4">
        <DialogHeader className="space-y-1 border-b px-2 pb-3">
          <DialogTitle className="text-base font-semibold">
            {previewTarget?.componentType ?? 'Component'} preview
          </DialogTitle>
          <DialogDescription className="truncate text-xs text-muted-foreground">
            {previewTarget?.componentId}
          </DialogDescription>
        </DialogHeader>
        <div ref={tileContainerRef} className="h-[calc(100vh-180px)] overflow-auto">
          {previewTarget && (
            <ComponentRenderer
              component={{ type: previewTarget.componentType, id: previewTarget.componentId }}
              containerId={`context-preview-${previewTarget.selectionKey}`}
              tileContainerRef={tileContainerRef}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// Scroll To Bottom Button
// ============================================================================

interface ScrollToBottomButtonProps {
  show: boolean
  onClick: () => void
}

function ScrollToBottomButton({ show, onClick }: ScrollToBottomButtonProps) {
  return (
    <div
      className={cn(
        'sticky bottom-4 flex justify-center transition-all duration-200',
        show ? 'opacity-100' : 'pointer-events-none opacity-0'
      )}
    >
      <Button variant="outline" size="icon" className="size-10 rounded-full bg-offwhite shadow-lg" onClick={onClick}>
        <ArrowDown className="size-5" />
      </Button>
    </div>
  )
}

const MAX_CONTEXT_RESPONSE_CHARS_PER_ITEM = 30000
const MAX_CONTEXT_ITEM_COUNT_PER_MESSAGE = 8
const MAX_CONTEXT_LABEL_CHARS = 120

function buildMessageWithContextFallback(displayText: string, contextItems: ChatContextPayloadItem[]): string {
  const baseText = displayText.trim()
  if (contextItems.length === 0) {
    return baseText
  }

  const fallbackLines = contextItems.map((item, index) => {
    const label = truncateText(item.title || item.componentId, MAX_CONTEXT_LABEL_CHARS)
    return `${index + 1}. [${item.selectionKey}] ${item.componentType} :: ${label}`
  })

  return `${baseText}\n\n[CONTEXT_REFERENCES]\n${fallbackLines.join('\n')}\n[/CONTEXT_REFERENCES]`
}

function buildContextPayloadItem(
  entry: ReturnType<typeof useAiChatComponentContext>['selectedContext'][number]
): ChatContextPayloadItem {
  const preview = getUiSchemaPreview(entry.response, entry.componentType)
  const responseDataSource = extractPreferredResponseData(entry.response)
  const { value: responseData, truncated } = sanitizeResponseForMessagePayload(
    responseDataSource,
    MAX_CONTEXT_RESPONSE_CHARS_PER_ITEM
  )

  return {
    selectionKey: entry.selectionKey,
    componentType: entry.componentType,
    componentId: entry.componentId,
    pageId: entry.pageId,
    title: preview.title,
    description: preview.description,
    filters: entry.filters,
    capturedAt: entry.capturedAt,
    responseData,
    truncated,
  }
}

function extractPreferredResponseData(response: unknown): unknown {
  if (!isRecord(response)) return response
  const data = response.data
  return data === undefined ? response : data
}

function sanitizeResponseForMessagePayload(
  responseData: unknown,
  maxChars: number
): { value: unknown; truncated: boolean } {
  try {
    const serialized = JSON.stringify(responseData)
    if (serialized.length <= maxChars) {
      return {
        value: JSON.parse(serialized) as unknown,
        truncated: false,
      }
    }
    return {
      value: {
        _truncated: true,
        _preview: serialized.slice(0, maxChars),
      },
      truncated: true,
    }
  } catch {
    return {
      value: {
        _truncated: true,
        _reason: 'Unserializable response data',
      },
      truncated: true,
    }
  }
}

function truncateText(value: string, maxChars: number): string {
  return value.length > maxChars ? `${value.slice(0, maxChars - 1)}…` : value
}
